import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const lastUpdated = 'August 18, 2026';

const deletedItems = [
  'Your account and profile',
  'All imported chess games and their analysis',
  'Remarkable moves detected in your games',
  'Puzzles and puzzle progress',
  'All training history and analytics',
  'Opening practice data',
  'Notification and push device tokens',
  'Your login identity (Auth0)'
];

const DeleteAccountPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top, rgba(46, 125, 50, 0.14), transparent 28%), linear-gradient(180deg, #F7F8F5 0%, #FFFFFF 38%, #F4F7FB 100%)'
      }}
    >
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#17351F' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Delete Account
          </Typography>
          <Link component={RouterLink} to="/support" color="inherit" sx={{ mr: 2 }}>
            Support
          </Link>
          <Link component={RouterLink} to="/privacy" color="inherit">
            Privacy Policy
          </Link>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 5,
            border: '1px solid rgba(34, 44, 55, 0.08)',
            boxShadow: '0 24px 80px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
            mb: 4
          }}
        >
          <Box
            sx={{
              p: { xs: 3, md: 5 },
              background:
                'linear-gradient(135deg, rgba(23, 53, 31, 0.98) 0%, rgba(39, 83, 48, 0.94) 48%, rgba(233, 240, 232, 0.94) 100%)',
              color: '#FFFFFF'
            }}
          >
            <Typography variant="overline" sx={{ letterSpacing: '0.12em', opacity: 0.8 }}>
              Account
            </Typography>
            <Typography variant="h3" component="h1" sx={{ mt: 1, mb: 1.5, color: '#FFFFFF' }}>
              Delete your ChessWiz account
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 700, color: 'rgba(255,255,255,0.82)' }}>
              This page explains how to delete your ChessWiz account and data, what
              gets removed, and how long the process takes. Account deletion is started from inside
              the app, or by emailing our support team if you can no longer access the app.
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.72)' }}>
              Last updated: {lastUpdated}
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack spacing={2.5}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  bgcolor: '#F7FAF7',
                  border: '1px solid rgba(46, 125, 50, 0.18)'
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <PhoneIphoneOutlinedIcon color="primary" />
                    <Typography variant="h5">How to request deletion</Typography>
                  </Stack>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
                    You request account deletion directly in the ChessWiz mobile app:
                  </Typography>
                  <List dense sx={{ mb: 1 }}>
                    <ListItem sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        <CheckCircleOutlineIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Open the ChessWiz app and go to Settings." />
                    </ListItem>
                    <ListItem sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        <CheckCircleOutlineIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary='Tap "Delete Account".' />
                    </ListItem>
                    <ListItem sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        <CheckCircleOutlineIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Confirm the request to start the deletion process." />
                    </ListItem>
                  </List>
                  <Typography variant="body1" color="text.secondary">
                    If you can no longer access the app, email{' '}
                    <Link href="mailto:support@chesswiz.net" underline="hover">
                      support@chesswiz.net
                    </Link>{' '}
                    from the email address tied to your account and ask us to delete your account.
                    We use that email to verify and locate your account.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<EmailOutlinedIcon />}
                      href="mailto:support@chesswiz.net"
                      sx={{ borderRadius: 999 }}
                    >
                      Email support
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <ScheduleOutlinedIcon color="primary" />
                  <Typography variant="h4">What happens next</Typography>
                </Stack>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Requesting deletion starts a <strong>30-day grace period</strong>. During this
                  time your account is deactivated but still recoverable, in case you change your
                  mind or requested deletion by mistake.
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  To cancel the deletion within the grace period, simply log back in to the
                  ChessWiz app and tap <strong>"Cancel deletion"</strong> on the banner shown at the
                  top of the screen. Your account and data are restored immediately.
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  After 30 days, the deletion becomes <strong>permanent and irreversible</strong>.
                  Once the grace period ends, your data cannot be recovered.
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <DeleteOutlineIcon color="primary" />
                  <Typography variant="h4">What data is deleted</Typography>
                </Stack>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  When the deletion becomes permanent, the following are erased:
                </Typography>
                <List dense>
                  {deletedItems.map((item) => (
                    <ListItem key={item} sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        <DeleteOutlineIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: '1px solid rgba(99, 115, 129, 0.16)'
                }}
              >
                <CardContent>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    What is kept and retention
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Nothing is retained after the deletion is complete. We do not keep anonymized
                    copies of your games, analysis, or profile.
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    The only retention is the 30-day grace period itself: your data is kept during
                    those 30 days so the deletion can be cancelled and your account restored. After
                    the grace period, all of the data listed above is permanently removed.
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center', pt: 2 }}>
          <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default DeleteAccountPage;
